import { z } from "zod";

/**
 * Allowed scopes for client credentials
 */
export const ClientCredentialsScopeSchema = z.enum(["credentials", "external"]);

export type ClientCredentialsScope = z.infer<
  typeof ClientCredentialsScopeSchema
>;

// ============================================================================
// Identity API Schemas (source of truth for upstream responses)
// These use Identity API field names: created/updated (not created_at/updated_at)
// ============================================================================

/**
 * Base fields shared across Identity API client credentials responses
 */
const IdentityClientCredentialsBaseSchema = z.object({
  client_id: z.string(),
  name: z.string(),
});

/**
 * Identity API response for listing client credentials
 */
export const IdentityClientCredentialsItemSchema =
  IdentityClientCredentialsBaseSchema.extend({
    allowed_scopes: z.array(z.string()).nullable().optional(),
    created: z.string().datetime().nullable().optional(),
    updated: z.string().datetime().optional(),
  });

/**
 * Identity API response for creating client credentials
 * Only returns client_id, client_secret, and name
 */
export const IdentityClientCredentialsCreatedItemSchema =
  IdentityClientCredentialsBaseSchema.extend({
    client_secret: z.string(),
  });

/**
 * Identity API paginated response for listing
 */
export const IdentityClientCredentialsListResponseSchema = z.object({
  count: z.number(),
  next: z.string().nullable().optional(),
  previous: z.string().nullable().optional(),
  results: z.array(IdentityClientCredentialsItemSchema),
});

export type IdentityClientCredentialsItem = z.infer<
  typeof IdentityClientCredentialsItemSchema
>;
export type IdentityClientCredentialsCreatedItem = z.infer<
  typeof IdentityClientCredentialsCreatedItemSchema
>;
export type IdentityClientCredentialsListResponse = z.infer<
  typeof IdentityClientCredentialsListResponseSchema
>;

// ============================================================================
// SDK API Schemas (for OpenAPI documentation and response types)
// These use SDK API field names: created_at/updated_at
// ============================================================================

/**
 * Base fields shared across SDK API client credentials responses
 */
const ClientCredentialsBaseSchema = z.object({
  client_id: z
    .string()
    .describe("The public identifier (Client ID) of the OAuth application"),
  name: z.string().describe("Human-readable name of the OAuth application"),
});

/**
 * Client credentials item returned in list responses
 * Note: client_secret is NOT included in list responses for security
 */
export const ClientCredentialsItemSchema = ClientCredentialsBaseSchema.extend({
  allowed_scopes: z
    .array(ClientCredentialsScopeSchema)
    .describe(
      "List of OAuth scopes that this application is allowed to request",
    ),
  created_at: z
    .string()
    .datetime()
    .nullable()
    .optional()
    .describe("When the application was created (ISO 8601)"),
  updated_at: z
    .string()
    .datetime()
    .optional()
    .describe("When the application was last updated (ISO 8601)"),
});

/**
 * Client credentials item returned when creating (includes client_secret)
 * The client_secret is only shown once on creation
 */
export const ClientCredentialsCreatedItemSchema =
  ClientCredentialsBaseSchema.extend({
    client_secret: z
      .string()
      .describe("The client secret (only shown once on creation)"),
  });

export type ClientCredentialsItem = z.infer<typeof ClientCredentialsItemSchema>;
export type ClientCredentialsCreatedItem = z.infer<
  typeof ClientCredentialsCreatedItemSchema
>;

/**
 * Query parameters for listing client credentials
 */
export const ListClientCredentialsQuerySchema = z
  .object({
    /** @deprecated Use page_size instead */
    pageSize: z.coerce
      .number()
      .int()
      .min(1)
      .max(100)
      .optional()
      .describe(
        "Deprecated: Use page_size instead. Number of items per page (default: 100, max: 100)",
      ),
    page_size: z.coerce
      .number()
      .int()
      .min(1)
      .max(100)
      .optional()
      .describe("Number of items per page (default: 100, max: 100)"),
    offset: z.coerce
      .number()
      .int()
      .min(0)
      .optional()
      .describe("Pagination offset"),
  })
  .describe("Query parameters for listing client credentials");

/**
 * Response schema for listClientCredentials
 */
export const ListClientCredentialsResponseSchema = z
  .object({
    data: z
      .array(ClientCredentialsItemSchema)
      .describe("Array of client credentials"),
    links: z
      .object({
        next: z
          .string()
          .nullable()
          .optional()
          .describe("URL for the next page of results (if available)"),
      })
      .describe("Pagination links"),
    meta: z
      .object({
        count: z.number().describe("Total number of items"),
        limit: z.number().describe("Number of items per page"),
        offset: z.number().describe("Offset of the current page"),
      })
      .describe("Pagination metadata"),
  })
  .describe("Response for listing client credentials");

export type ListClientCredentialsQuery = z.infer<
  typeof ListClientCredentialsQuerySchema
>;
export type ListClientCredentialsResponse = z.infer<
  typeof ListClientCredentialsResponseSchema
>;

/**
 * Request body for creating client credentials
 */
export const CreateClientCredentialsRequestSchema = z
  .object({
    name: z
      .string()
      .min(1)
      .max(255)
      .describe("Human-readable name for the client credentials"),
    allowed_scopes: z
      .array(ClientCredentialsScopeSchema)
      .min(1)
      .describe(
        "List of OAuth scopes that this application will be allowed to request",
      ),
    // Policy validation is performed by Identity — sdkapi passes it through as-is.
    policy: z
      .record(z.string(), z.unknown())
      .optional()
      .describe("Policy document to attach to the credentials"),
  })
  .describe("Request body for creating client credentials");

/**
 * Response schema for createClientCredentials
 */
export const CreateClientCredentialsResponseSchema = z
  .object({
    data: ClientCredentialsCreatedItemSchema.describe(
      "The created client credentials (includes client_secret)",
    ),
  })
  .describe("Response for creating client credentials");

export type CreateClientCredentialsRequest = z.infer<
  typeof CreateClientCredentialsRequestSchema
>;
export type CreateClientCredentialsResponse = z.infer<
  typeof CreateClientCredentialsResponseSchema
>;

/**
 * Path parameters for deleting client credentials
 */
export const DeleteClientCredentialsParamSchema = z.object({
  clientId: z.string().describe("The client ID of the credentials to delete"),
});

export type DeleteClientCredentialsParam = z.infer<
  typeof DeleteClientCredentialsParamSchema
>;
